import React, { useState } from "react";
import { Link } from "react-router-dom";

import { premiumReports } from "../api";
import { formatDate } from "../api/formatDate";
import "../assets/styles/pages/multiReports.scss";
import MainStructure from "../components/containers/MainStructure";
import Pagination from "../components/elements/Pagination";
import SourAlert from "../components/elements/SourAlert";
import usePaginatedData from "../hooks/usePaginatedData";
import useSourAlert from "../hooks/useSourAlert";

function getMultiReportStatus(report) {
	if (report?.status === "completed") {
		return { label: "Ready", modifier: "ready" };
	}
	if (report?.status === "error") {
		return { label: "Needs attention", modifier: "error" };
	}
	if (report?.status === "requested") {
		return { label: "Queued", modifier: "pending" };
	}
	return { label: "Generating", modifier: "pending" };
}

function PremiumPlan() {
	const [deletedIds, setDeletedIds] = useState([]);
	const [alertProps, showAlert] = useSourAlert();
	const {
		data,
		loading,
		currentPage,
		totalPages,
		handlePageChange,
		startIndex,
		endIndex,
	} = usePaginatedData(premiumReports.details, { limit: 10 });
	const visibleReports = data.results.filter((report) => !deletedIds.includes(report.id));

	function handleDelete(report) {
		showAlert({
			type: "warning",
			title: "Delete multi report",
			description: `Delete ${report.name || "this multi report"}? This cannot be undone.`,
			showCancelButton: true,
			cancelButtonText: "Cancel",
			showOkButton: true,
			okButtonText: "Delete",
			onOk: () => {
				premiumReports
					.delete_report(report.id)
					.then(() => setDeletedIds((previous) => [...previous, report.id]))
					.catch(() => {
						showAlert({
							type: "error",
							title: "Report not deleted",
							description: "We could not delete this report. Please try again.",
						});
					});
			},
		});
	}

	return (
		<MainStructure>
			<main className="multi-reports">
				<header className="multi-reports__hero">
					<div>
						<p className="multi-reports__eyebrow">Batch pedigree analysis</p>
						<h1>Multi reports</h1>
						<p>Explore every combination, discover the strongest pedigrees, and compare horses side by side.</p>
					</div>
					<Link to="/new-premium" className="multi-reports__create">
						<i className="fa fa-plus" aria-hidden="true" /> Create multi report
					</Link>
				</header>

				<section className="multi-reports__panel" aria-labelledby="multi-report-history-title">
					<div className="multi-reports__panel-header">
						<div>
							<h2 id="multi-report-history-title">Report history</h2>
							<p>{data.count === 1 ? "1 multi report" : `${data.count} multi reports`}</p>
						</div>
						{!loading && visibleReports.length ? (
							<div className="multi-reports__pagination">
								<span>{startIndex}–{endIndex} of {data.count}</span>
								<Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
							</div>
						) : null}
					</div>

					{loading ? (
						<div className="multi-reports__state" role="status">
							<span className="multi-reports__spinner" /> Loading multi reports…
						</div>
					) : visibleReports.length ? (
						<>
							<div className="multi-report-list__labels" aria-hidden="true">
								<span>Report</span><span>Status</span><span>Actions</span>
							</div>
							<ul className="multi-report-list">
								{visibleReports.map((report) => {
									const name = report.name || "Untitled multi report";
									const status = getMultiReportStatus(report);
									const reportPath = report.status === "completed" && report.id ? `/reports/collections/premium/${report.id}` : null;
									return (
										<li key={report.id}>
											<span className="multi-report-list__icon"><i className="fa fa-layer-group" aria-hidden="true" /></span>
											<span className="multi-report-list__details">
												<strong>{reportPath ? <Link to={reportPath}>{name}</Link> : name}</strong>
												<span>Created {formatDate(report.created)}</span>
											</span>
											<span className={`multi-report-status multi-report-status--${status.modifier}`}>{status.label}</span>
											<span className="multi-report-list__actions">
												{reportPath ? (
													<Link
														to={reportPath}
														aria-label={`View ${name} report`}
														title="View and compare horses"
													>
														<i className="fa fa-eye" aria-hidden="true" />
													</Link>
												) : null}
												{!reportPath && status.modifier === "pending" ? (
													<span
														className="is-waiting"
														aria-label="Report not ready"
														title="Report not ready"
													>
														<i className="fa fa-clock" aria-hidden="true" />
													</span>
												) : null}
												<button type="button" onClick={() => handleDelete(report)} aria-label={`Delete ${name}`} title="Delete report">
													<i className="fa fa-trash" aria-hidden="true" />
												</button>
											</span>
										</li>
									);
								})}
							</ul>
							{totalPages > 1 ? (
								<div className="multi-reports__pagination multi-reports__pagination--footer">
									<span>{startIndex}–{endIndex} of {data.count}</span>
									<Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
								</div>
							) : null}
						</>
					) : (
						<div className="multi-reports__state multi-reports__state--empty">
							<i className="fa fa-layer-group" aria-hidden="true" />
							<h3>No multi reports yet</h3>
							<p>Create a request to compare multiple sire and dam combinations at once.</p>
							<Link to="/new-premium">Create your first multi report</Link>
						</div>
					)}
				</section>
			</main>
			<SourAlert {...alertProps} />
		</MainStructure>
	);
}

export default PremiumPlan;
